import { DimensionType, MetricType } from '../types/field';
/**
 * Semantic model for the NBFC delinquency/NPA forecast output table.
 *
 * This is a *derived* table: it has no external connector or CSV owner, and
 * its rows are computed (a statsmodels time-series forecast run in the dlt
 * Python runner, packages/backend/src/dlt/forecasting.py), not extracted.
 * It never matches `nbfcLoanBookPack` — a forecast row's columns
 * (period/metric_name/predicted_value/confidence bounds) share nothing with
 * a loan-level snapshot's columns (loan_id/outstanding_principal/DPD) — so it
 * needs its own tiny pack, following the same DomainPack shape.
 *
 * One row is one (period, metric_name) forecast point. `metric_name` values
 * are the same current-state metric names the forecast projects forward:
 * 'delinquency_rate' or 'gross_npa_ratio' (see nbfcLoanBookPack). Labels in
 * the frontend must state plainly these are statistical forecasts with
 * confidence intervals, not the deterministic current-state risk bands.
 */
export const nbfcForecastPack = {
    id: 'nbfc_forecast',
    name: 'NBFC delinquency forecast',
    description: "Forward-looking delinquency/NPA forecast with confidence intervals, computed by time-series statistical modeling over an NBFC loan book's historical monthly metrics.",
    // Every landed row is a real forecast point already computed by the
    // job; there is no closed/void population to exclude.
    populationFilter: null,
    /** One row per metric per period: neither alone is unique. */
    grain: ['period', 'metric_name'],
    columns: [
        {
            key: 'period',
            synonyms: ['period', 'forecast_period', 'forecast_month'],
            type: DimensionType.DATE,
            required: true,
            description: 'The forecasted month, as the first day of that month.',
        },
        {
            key: 'metric_name',
            synonyms: ['metric_name', 'metric'],
            type: DimensionType.STRING,
            required: true,
            description: "Which current-state metric this row forecasts, e.g. 'delinquency_rate' or 'gross_npa_ratio'.",
        },
        {
            key: 'predicted_value',
            synonyms: ['predicted_value', 'forecast_value', 'point_forecast'],
            type: DimensionType.NUMBER,
            required: true,
            description: 'The point forecast for metric_name at period.',
        },
        {
            key: 'ci_lower',
            synonyms: ['ci_lower', 'confidence_lower', 'lower_bound'],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Lower bound of the forecast confidence interval. Never chart predicted_value without this.',
        },
        {
            key: 'ci_upper',
            synonyms: ['ci_upper', 'confidence_upper', 'upper_bound'],
            type: DimensionType.NUMBER,
            required: false,
            description: 'Upper bound of the forecast confidence interval. Never chart predicted_value without this.',
        },
        {
            key: 'model_name',
            synonyms: ['model_name', 'model'],
            type: DimensionType.STRING,
            required: false,
            description: 'Statistical model that produced this forecast (e.g. Holt-Winters exponential smoothing). Never an LLM.',
        },
        {
            key: 'forecast_generated_at',
            synonyms: ['forecast_generated_at', 'generated_at'],
            type: DimensionType.TIMESTAMP,
            required: false,
            description: 'When this forecast run computed the row.',
        },
    ],
    dimensions: [],
    metrics: [
        {
            name: 'average_predicted_value',
            label: 'Average predicted value',
            description: 'Mean point forecast. Grouped by period and metric_name, each group is normally exactly one row.',
            type: MetricType.AVERAGE,
            sql: '${col.predicted_value}',
            format: null,
            round: 4,
        },
        {
            name: 'average_ci_lower',
            label: 'Average confidence lower bound',
            description: 'Mean lower confidence bound.',
            type: MetricType.AVERAGE,
            sql: '${col.ci_lower}',
            format: null,
            round: 4,
        },
        {
            name: 'average_ci_upper',
            label: 'Average confidence upper bound',
            description: 'Mean upper confidence bound.',
            type: MetricType.AVERAGE,
            sql: '${col.ci_upper}',
            format: null,
            round: 4,
        },
        {
            name: 'forecast_point_count',
            label: 'Forecast points',
            description: 'Number of forecasted (period, metric) points.',
            type: MetricType.COUNT,
            sql: '${col.period}',
            format: null,
            round: null,
        },
    ],
};
//# sourceMappingURL=nbfcForecast.js.map